import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    InputNumber,
    Card,
    Typography,
    message,
    Space,
    Descriptions,
    Tag,
    Spin,
    Select
} from 'antd';
import { QrcodeOutlined, PlusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import moment from 'moment';
import fileDownload from 'js-file-download';
import debounce from 'lodash/debounce'; // 需要确保安装了 lodash

const { Title, Text } = Typography;
const { Option } = Select;

const HairDyeStock = () => {
    const [stock, setStock] = useState([]);
    const [dyeInfo, setDyeInfo] = useState(null);
    const [specs, setSpecs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    
    // 商店列表相关状态
    const [storeState, setStoreState] = useState({
        list: [],
        page: 1,
        total: 0,
        loading: false,
        keyword: '',
        hasMore: true
    });
    
    const [storeId, setStoreId] = useState(0); // Default to 0 (Unbound)
    const [form] = Form.useForm();
    const { dyeId } = useParams();
    const navigate = useNavigate();
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    // 简单的防抖函数实现，如果项目中没有 lodash
    const useDebounce = (callback, delay) => {
        const timer = useRef(null);
        return useCallback((...args) => {
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(() => callback(...args), delay);
        }, [callback, delay]);
    };

    const fetchDyeInfo = async () => {
        try {
            const response = await apiService.hairDye.getById(dyeId);
            if (response.code === 200 && response.data) {
                setDyeInfo(response.data);
            } else {
                message.error('获取染膏信息失败');
            }
        } catch (error) {
            // Error handled by interceptor
        }
    };

    const fetchSpecs = async () => {
        try {
            const response = await apiService.hairDye.getSpecs(dyeId);
            if (response.code === 200) {
                const records = Array.isArray(response.data) ? response.data : [];
                setSpecs(records);
                const defaultSpec = records.find(item => item.isDefault) || records[0];
                if (defaultSpec) {
                    form.setFieldsValue({ specId: defaultSpec.id });
                }
            } else {
                message.error(response.message || '获取规格列表失败');
            }
        } catch (error) {
            // Error handled by interceptor
        }
    };

    const fetchStores = async (page = 1, keyword = '', append = false) => {
        if (storeState.loading) return;
        
        setStoreState(prev => ({ ...prev, loading: true }));
        
        try {
            const params = { pageNum: page, pageSize: 20, keyword }; // 假设后端支持 keyword 参数
            const response = await apiService.store.getList(params);
            
            if (response.code === 200) {
                const newStores = response.data.records || [];
                const total = response.data.total || 0;
                
                setStoreState(prev => ({
                    ...prev,
                    list: append ? [...prev.list, ...newStores] : newStores,
                    page: page,
                    total: total,
                    loading: false,
                    hasMore: (page * 20) < total
                }));
            } else {
                setStoreState(prev => ({ ...prev, loading: false }));
            }
        } catch (error) {
            setStoreState(prev => ({ ...prev, loading: false }));
        }
    };

    const handleStoreScroll = (e) => {
        const { target } = e;
        // 滚动到底部 (预留 10px 容差)
        if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 10 && !storeState.loading && storeState.hasMore) {
            fetchStores(storeState.page + 1, storeState.keyword, true);
        }
    };

    const handleStoreSearch = useDebounce((value) => {
        setStoreState(prev => ({ ...prev, keyword: value, list: [], page: 1, hasMore: true }));
        fetchStores(1, value, false);
    }, 500);

    const fetchStock = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const params = { pageNum: page, pageSize };
            if (storeId) {
                params.storeId = storeId;
            }
            const response = await apiService.hairDye.getStockList(dyeId, params);
            if (response.code === 200 && response.data && Array.isArray(response.data.records)) {
                const validRecords = response.data.records.filter(record => record && record.id);
                setStock(validRecords);
                setPagination({
                    current: response.data.current,
                    pageSize: response.data.size,
                    total: response.data.total,
                });
            } else {
                message.error(response.message || '获取库存列表失败');
            }
        } catch (error) {
            // Error handled by interceptor
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDyeInfo();
        fetchSpecs();
        fetchStores(1, '', false);
    }, [dyeId]);

    useEffect(() => {
        fetchStock();
    }, [dyeId, storeId]);

    const handleTableChange = (pagination) => {
        fetchStock(pagination.current, pagination.pageSize);
    };

    const handleGenerate = async (values) => {
        try {
            const response = await apiService.hairDye.generateStock(dyeId, {
                count: values.count,
                specId: values.specId
            });
            if (response.code === 200) {
                const { startId, endId } = response.data;
                setModalVisible(false);
                fetchStock(); // Refresh list

                Modal.confirm({
                    title: '生成成功',
                    content: `成功生成 ${values.count} 条库存记录。是否立即导出这些二维码？`,
                    okText: '是，立即导出',
                    cancelText: '否',
                    onOk: () => handleExport({ startId, endId }),
                });
            } else {
                message.error(response.message || '生成失败');
            }
        } catch (error) {
            // Error handled by interceptor
        }
    };

    const handleExport = async (range = null) => {
        let params = {};
        
        if (range && range.startId && range.endId) {
            // Export by range (from batch generation)
            params = { startId: range.startId, endId: range.endId };
        } else {
            // Export by selection
            if (selectedRowKeys.length === 0) {
                message.warning('请至少选择一条记录进行导出');
                return;
            }
            params = { ids: selectedRowKeys };
        }

        try {
            const response = await apiService.hairDye.exportQrCodes(params);
            const fileName = `qrcodes_${dyeInfo?.dyeName || dyeId}_${moment().format('YYYYMMDDHHmmss')}.zip`;
            fileDownload(response, fileName);
            message.success('二维码已开始下载');
            if (!range) {
                setSelectedRowKeys([]); // Clear selection after export
            }
        } catch (error) {
            message.error('导出失败');
        }
    };

    const onSelectChange = (keys) => {
        setSelectedRowKeys(keys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const columns = [
        { title: '库存ID', dataIndex: 'id', key: 'id' },
        { title: '染膏唯一码', dataIndex: 'dyeCode', key: 'dyeCode' },
        {
            title: '规格',
            dataIndex: 'specName',
            key: 'specName',
            render: (text, record) => text || (record.capacity ? `${record.capacity}${record.unit || 'g'}` : '-'),
        },
        {
            title: '绑定店铺',
            dataIndex: 'storeName',
            key: 'storeName',
            render: (text) => text || <Text type="secondary">暂未绑定</Text>,
        },
        {
            title: '绑定时间',
            dataIndex: 'updateTime',
            key: 'updateTime',
            render: (text, record) => record.storeName ? moment(text).format('YYYY-MM-DD HH:mm') : <Text type="secondary">暂未绑定</Text>,
        },
        {
            title: '创建时间',
            dataIndex: 'createTime',
            key: 'createTime',
            render: (text) => moment(text).format('YYYY-MM-DD HH:mm'),
        },
    ];

    return (
        <Card>
            <Space style={{ marginBottom: 20, width: '100%', justifyContent: 'space-between' }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                    返回染膏管理
                </Button>
                <Space>
                    <span>所属店铺：</span>
                    <Select
                        value={storeId}
                        onChange={setStoreId}
                        style={{ width: 250 }}
                        showSearch
                        placeholder="选择店铺"
                        optionFilterProp="children"
                        onPopupScroll={handleStoreScroll}
                        onSearch={handleStoreSearch}
                        filterOption={false} // 禁用本地过滤，使用服务端搜索
                        notFoundContent={storeState.loading ? <Spin size="small" /> : null}
                    >
                        <Option value={0}>暂未绑定</Option>
                        {storeState.list.map(store => (
                            <Option key={store.id} value={store.id}>{store.storeName}</Option>
                        ))}
                        {storeState.loading && (
                            <Option key="loading" disabled>
                                <Spin size="small" /> 加载中...
                            </Option>
                        )}
                    </Select>
                </Space>
            </Space>

            {dyeInfo ? (
                <Descriptions title="染膏信息" bordered column={2} style={{ marginBottom: 20 }}>
                    <Descriptions.Item label="ID">{dyeInfo.id}</Descriptions.Item>
                    <Descriptions.Item label="名称">{dyeInfo.dyeName}</Descriptions.Item>
                    <Descriptions.Item label="品牌">{dyeInfo.brand}</Descriptions.Item>
                    <Descriptions.Item label="色号">{dyeInfo.colorNumber}</Descriptions.Item>
                </Descriptions>
            ) : <Spin />}

            <Title level={4}>库存管理</Title>
            <Space style={{ marginBottom: 20 }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                    const defaultSpec = specs.find(item => item.isDefault) || specs[0];
                    form.setFieldsValue({ specId: defaultSpec?.id });
                    setModalVisible(true);
                }} disabled={specs.length === 0}>
                    批量生成
                </Button>
                <Button icon={<QrcodeOutlined />} onClick={() => handleExport()} disabled={selectedRowKeys.length === 0}>
                    导出二维码
                </Button>
            </Space>

            <Table
                rowSelection={rowSelection}
                columns={columns}
                dataSource={stock}
                rowKey="id"
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ x: 'max-content' }}
            />

            <Modal
                title="批量生成库存"
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={() => form.submit()}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleGenerate}>
                    <Form.Item
                        name="specId"
                        label="染膏规格"
                        rules={[{ required: true, message: '请选择染膏规格' }]}
                    >
                        <Select placeholder="请选择500g或900g规格">
                            {specs.map(spec => (
                                <Option key={spec.id} value={spec.id}>
                                    {spec.specName || `${spec.capacity}${spec.unit || 'g'}`}
                                    {spec.isDefault ? '（默认）' : ''}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="count"
                        label="生成数量"
                        rules={[{ required: true, message: '请输入要生成的数量' }]}
                    >
                        <InputNumber min={1} max={1000} style={{ width: '100%' }} placeholder="一次最多可生成1000条" />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default HairDyeStock;
